/**
 * Tiny line-level diff shared by the Design tab's Hammer review (renderer) and
 * the Foundry build-update changelog (GUI main). No `diff` dependency (Anvil
 * TDD §7.5): a longest-common-subsequence over LINES, emitted as a unified row
 * stream (red-delete / green-add / context).
 *
 * O(m·n) time + space — fine for section-sized markdown (hundreds of lines);
 * inputs here are a single GDD/PRD section body, never a whole repo.
 */
/**
 * Line diff of `before` → `after`. Unchanged lines come back as `context`,
 * lines only in `before` as `del`, lines only in `after` as `add`, in an
 * order that reads top-to-bottom like a unified diff.
 */
export function diffLines(before, after) {
    const a = before.split('\n');
    const b = after.split('\n');
    const m = a.length;
    const n = b.length;
    // LCS length table: dp[i][j] = LCS of a[i:] and b[j:].
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i -= 1) {
        for (let j = n - 1; j >= 0; j -= 1) {
            dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }
    const rows = [];
    let i = 0;
    let j = 0;
    while (i < m && j < n) {
        if (a[i] === b[j]) {
            rows.push({ type: 'context', text: a[i] });
            i += 1;
            j += 1;
        }
        else if (dp[i + 1][j] >= dp[i][j + 1]) {
            // Dropping a[i] keeps at least as much of the LCS — it's a deletion.
            rows.push({ type: 'del', text: a[i] });
            i += 1;
        }
        else {
            rows.push({ type: 'add', text: b[j] });
            j += 1;
        }
    }
    while (i < m) {
        rows.push({ type: 'del', text: a[i] });
        i += 1;
    }
    while (j < n) {
        rows.push({ type: 'add', text: b[j] });
        j += 1;
    }
    return rows;
}
/**
 * Render a unified diff as a fenced-`diff` markdown block (`- ` deletions,
 * `+ ` additions, `  ` context). Used by the build-update changelog so the
 * agent — and the review modal's markdown renderer — see standard diff syntax.
 * Returns an empty string when the bodies are identical.
 */
export function renderUnifiedDiff(before, after) {
    const rows = diffLines(before, after);
    if (rows.every((r) => r.type === 'context'))
        return '';
    const body = rows
        .map((r) => `${r.type === 'add' ? '+ ' : r.type === 'del' ? '- ' : '  '}${r.text}`)
        .join('\n');
    return `\`\`\`diff\n${body}\n\`\`\``;
}
//# sourceMappingURL=line-diff.js.map